/**
 * Every exploration on this device.
 *
 * This sheet exists because the capability did and the surface did not. Every
 * change has always been written to disk within about a second, and the stored
 * graphs have always been openable — but the only way in was four rows buried in
 * the command palette, and an autosave nobody can see is indistinguishable from no
 * autosave. A user who cannot tell asks for a save button, which is the wrong fix:
 * the right one is to show them what is already there.
 *
 * So the sheet opens with the state of the thing they were worried about — saved,
 * and when — and then lists what that saving has produced. Two destructive actions
 * live here and both are confirmed in place, because a silent way to lose an
 * exploration is one too many.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { lockHeld, useStore } from '../core/store'
import { libraryLedger, listRecent, storageReport, type RecentEntry } from '../core/persist'
import { canForgetEverything, forgetEverything, forgetExploration } from '../core/forget'
import {
  clearForgetAllAsk,
  forgetAllLine,
  forgetAllSentence,
  forgetLine,
  forgetSentence,
  useForgetAllAsk,
  useForgetting,
  useForgettingEverything,
} from './forgetPress'
import { shelfOpen, storageEstimate } from '../core/docStore'
import {
  MIN_SCORED,
  scoredShortfall,
  verdictOf,
  type CalibrationAgainst,
  type ModelCalibration,
} from '../core/calibration'
import { stabilityLine } from '../core/panel'
import { FOUND_WORD } from '../core/vocabulary'
import { openExplorationFile, openSession } from '../core/autosave'
import { readExplorationFile } from '../core/explorationFile'
import { forkLine } from '../core/name'
import { Ago, useAgo } from './components/ago'
import { trackRecordLine } from '../core/trackRecord'
import { Icon } from './components/Icon'
import { useModalFocus } from './components/useModalFocus'
import { seatWhenFilled } from './Grounding'

/** The ledger's heading, for the one press that opens this sheet at it. */
const LEDGER_ID = 'library-ledger'

/** What a row here is called: the given name, else the whole seed (D105). */
const titleOf = (e: RecentEntry): string => e.name || e.seed || 'Untitled exploration'

const fmtSize = (chars: number): string =>
  chars < 1000 ? `${chars} characters` : `${Math.round(chars / 1000)} thousand characters`
/** Bytes from the browser's own storage estimate, at the size that reads well. */
const fmtBytes = (bytes: number): string =>
  bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(bytes / 1e6))} MB`

/**
 * One calibration table, per model, for either kind of evidence.
 *
 * One component and two call sites rather than two copies of the rows: the arithmetic
 * is shared (see `CalibrationRow`), so the presentation has to be, or the weaker table
 * drifts into reading like the stronger one. What is NOT shared is the heading, the
 * noun and the caveat — those are the whole difference between "you observed this" and
 * "a search came back".
 */
function CalibrationTable({
  ledger,
  against,
}: {
  ledger: ModelCalibration[]
  against: CalibrationAgainst
}) {
  return (
    <>
      {ledger.map((c) => (
        <div key={c.key} className="docs-row" style={{ alignItems: 'flex-start' }}>
          <b style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s2)' }}>
            <span>{c.model.name}</span>
            {c.brier === null && <span className="mrow-lack">still filling</span>}
          </b>
          <span className="srcs-meta">
            {/* Numerators, never a bare percentage: "9 of 16" carries its own
                sample size and "56%" does not. */}
            {c.brier === null ? (
              /* The remainder, in the line the scored row keeps its verdict on.
                 This state is the normal one — five recorded results means five
                 trips to a lab — and a row that only prints counts reads as a
                 feature that never runs rather than as a meter filling. */
              <>
                {scoredShortfall(c)}
                {c.inconclusive > 0 &&
                  ` · ${c.inconclusive} where ${against === 'tested' ? 'the result' : 'the search'} settled nothing`}
              </>
            ) : (
              <>
                {/* "Held up" is the table's word for a result that went the
                    claim's way — the run's end card and the report's section
                    say it of the same record (`core/heldUp.ts`). */}
                {c.supported} of {c.scored}{' '}
                {against === 'tested' ? FOUND_WORD.heldUp.toLowerCase() : 'read as supported'}
                {c.inconclusive > 0 && ` · ${c.inconclusive} settled nothing`}
                {` · across ${c.explorations} exploration${c.explorations === 1 ? '' : 's'}`}
                <br />
                {verdictOf(c, against)}
              </>
            )}
          </span>
        </div>
      ))}
    </>
  )
}

export function Library() {
  const open = useStore((s) => s.libraryOpen)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useModalFocus(dialogRef, open)
  const setOpen = useStore((s) => s.setLibrary)
  const setExport = useStore((s) => s.setExport)
  const currentId = useStore((s) => s.session.id)
  /** The open exploration's name off the live session, as the sidebar reads it:
   *  a Rename lands in the store a save ahead of the index row. */
  const openName = useStore((s) => s.session.name)
  const savedAt = useStore((s) => s.savedAt)
  // The Library's own age words were a third copy ("3 hours ago", then the UTC
  // day) beside the sidebar's "3h ago" for the same save; one form, kept true.
  const savedAge = useAgo(savedAt ?? null)
  const saveState = useStore((s) => s.saveState)
  const saveError = useStore((s) => s.saveError)
  const nodeCount = useStore((s) => s.session.order.length)
  const toast = useStore((s) => s.toast)

  const [rows, setRows] = useState<RecentEntry[]>([])
  /** The browser's own estimate of the origin's room, asked when the sheet opens.
   *  Null where the API is missing, and then the sentence below says only what
   *  is stored. There is no budget of this app's own any more — the documents
   *  live in IndexedDB, and the origin's quota is the only bound. */
  const [room, setRoom] = useState<{ usage: number; quota: number } | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [confirmAll, setConfirmAll] = useState(false)
  // Pending until the shelf answers (D331): the armed row, and the bulk door.
  const forgettingOne = useForgetting(confirming)
  const forgettingAll = useForgettingEverything()
  /** Whether a provider key is stored — the one thing Forget everything leaves,
   *  on purpose, and so the one thing its sentence has to name (D335). A
   *  boolean, so the selector answers with a value the store already had. */
  const keysKept = useStore((s) => Object.values(s.keys).some(Boolean))
  const fileRef = useRef<HTMLInputElement | null>(null)
  /** The confirm just armed, so its Keep can take the focus the press that
   *  armed it lost: the press is replaced by the confirm, and focus fell to
   *  `<body>` — a keyboard reader was left nowhere, beside an act nobody can
   *  undo (D335). Keep, because the safe answer is the default one. */
  const keepRef = useRef<HTMLButtonElement | null>(null)

  const refresh = () => {
    setRows(listRecent())
    void storageEstimate().then(setRoom)
  }
  useEffect(() => {
    if (open) {
      refresh()
      setConfirming(null)
      setConfirmAll(false)
    }
  }, [open])

  const space = useMemo(() => (open ? storageReport() : null), [open, rows])
  // Recomputed with `rows`, so forgetting an exploration drops its results out of
  // the ledger in the same breath it leaves the list. The OPEN exploration is read
  // live rather than off the shelf, which is up to a debounce behind it: the press
  // under a result that was recorded a second ago opens this sheet, and the row it
  // counted toward has to be here with that result in it. All three tables come
  // from the one call the export and the Overview read (`libraryLedger`), so none
  // of the three surfaces can count a result the others have not.
  const tables = useMemo(
    () => (open ? libraryLedger(useStore.getState().session) : null),
    [open, rows],
  )
  const ledger = tables?.calibration ?? []
  // "See the ledger" opens the sheet at the table, once; any other door opens it
  // at the top, as it always has.
  const ledgerAsk = useStore((s) => s.ledgerAsk)
  useEffect(() => {
    if (!open || !ledgerAsk) return
    useStore.getState().clearLedgerAsk()
    seatWhenFilled(
      () => document.getElementById(LEDGER_ID) ?? undefined,
      () => true,
    )
  }, [open, ledgerAsk])
  // The palette's Forget door opens the sheet with Forget everything armed, once
  // — the ledger's shape (D335). After the reset above, which runs first on the
  // same commit, so an opening that was asked arms and any other opening does not.
  const forgetAllAsk = useForgetAllAsk()
  useEffect(() => {
    if (!open || !forgetAllAsk) return
    clearForgetAllAsk()
    setConfirming(null)
    setConfirmAll(true)
    seatWhenFilled(
      () => keepRef.current ?? undefined,
      () => true,
    )
  }, [open, forgetAllAsk])
  useEffect(() => {
    if (!open || (confirming === null && !confirmAll)) return
    const at = document.activeElement
    if (at && at !== document.body && at.isConnected && at !== dialogRef.current) return
    keepRef.current?.focus()
    // On whether there are rows too: the confirm is drawn only over rows, and the
    // palette's ask arms it a commit before the index has been read in.
  }, [open, confirming, confirmAll, rows.length > 0])
  // The second table, from readings rather than results. A separate list, never
  // concatenated: a recorded result outranks a reading, and one figure averaging
  // the two would answer neither question.
  const checkLedger = tables?.checkCalibration ?? []
  // The third table, and a different question from both of those — how far three
  // blind passes of one model landed apart. Its own list for the same reason the
  // second one has one: nothing here may be averaged with a calibration figure,
  // because steadiness is not accuracy.
  const stability = tables?.stability ?? []
  // The fourth, and a count rather than a score: how the conjectures a model
  // proposed have fared where somebody tested them. Its own list, never merged —
  // a prediction going the way it said is not a confidence being calibrated.
  const conjectures = tables?.trackRecord ?? []

  if (!open) return null

  const listed = rows.map((e) => (e.id === currentId ? { ...e, name: openName } : e))

  /**
   * Opening anything swaps the canvas, so a pass or a live run holds every door —
   * except the worked example's unpaid unfurl, which is stopped rather than
   * waited for (`yieldUnpaid`). The run is asked first, and the unfurl is stopped
   * only when `yieldDemo` says the open is about to go through, so a file that
   * turns out to be unreadable never stops it. Asked of the store, not this
   * render: a file is read across an await.
   */
  const held = (yieldDemo: boolean): boolean => {
    const now = useStore.getState()
    const live = now.run !== null && now.run.phase !== 'ended'
    if (!live && (yieldDemo ? now.yieldUnpaid() : !lockHeld(now))) return false
    toast(
      live
        ? 'A run is going. Stop it before opening another exploration — it would stop itself anyway.'
        : 'One thought at a time — let the current branch finish.',
      'warn',
    )
    return true
  }

  const openOne = (id: string) => {
    if (held(true)) return
    setOpen(false)
    openSession(id)
  }

  /**
   * The way back in for the two files this app writes: the export sheet's JSON
   * and the recovery copy. `readExplorationFile` reads either through the one
   * storage codec, and each exploration lands as a new copy beside whatever is
   * stored — see `openExplorationFile` for why a file's own id never does.
   */
  const openFile = async (file: File) => {
    if (held(false)) return
    let text: string
    try {
      text = await file.text()
    } catch {
      // A file that moved on disk between the pick and the read. Not "neither":
      // nothing is known about what it holds.
      toast('The browser could not finish reading that file. Try choosing it again.', 'bad')
      return
    }
    const read = readExplorationFile(text)
    if (read.kind !== 'ok') {
      toast(
        'Pathways can open only an exploration’s JSON export or a recovery copy, and that file is neither.',
        'bad',
      )
      return
    }
    if (held(true)) return
    const done = openExplorationFile(read.docs)
    if (!done) return
    setOpen(false)
    const seed = done.opened.session.seed || 'Untitled exploration'
    toast(
      `Opened a copy of “${seed.slice(0, 80)}${seed.length > 80 ? '…' : ''}” from the file.` +
        (done.added
          ? ` ${done.added} more ${done.added === 1 ? 'exploration it held is' : 'explorations it held are'} in the list.`
          : '') +
        (read.unreadable
          ? ` ${read.unreadable} ${read.unreadable === 1 ? 'document' : 'documents'} in it could not be read.`
          : ''),
      read.unreadable ? 'warn' : 'good',
    )
  }

  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false)
      }}
    >
      <div
        className="sheet sheet--tall panel"
        role="dialog"
        aria-modal="true"
        aria-label="Library"
        ref={dialogRef}
        tabIndex={-1}
      >
        {/* Named as its door is. The sidebar's row says Library and the palette's
            does too; a sheet titled "Explorations" behind a door called Library
            read as a different room, and "Explorations" already names the pane
            the door is in. The glyph is the door's, for the same reason. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="library" size={18} strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
          <h2>Library</h2>
        </div>

        <div className="sheet-body scroll">
          {/* The answer to "how do I save this", first, because it is the question
              this sheet is most often opened to ask. */}
          <p className="gnote">
            {saveState === 'failed'
              ? saveError
              : nodeCount > 0 && !shelfOpen()
                ? 'This exploration is held in memory. This browser could not open its storage; export a copy before closing the tab.'
                : saveState === 'saving' || saveState === 'pending'
                  ? 'Saving changes. Keep this page open until the save completes.'
                  : nodeCount > 0
                    ? savedAt
                      ? `Saved in this browser ${savedAge}. Changes are saved automatically; a save is complete only after storage confirms it.`
                      : 'Changes save automatically in this browser.'
                    : 'Nothing to save yet. Start an exploration to keep it in this browser.'}
          </p>
          <p className="gnote gnote--quiet">
            Stored in this browser only. Nothing is uploaded anywhere, which also means clearing
            site data or opening the app in another browser loses it — the share or export below is
            how a copy leaves this machine, and opening a Pathways file is how it comes back.
          </p>

          <div style={{ display: 'flex', gap: 'var(--s2)', flexWrap: 'wrap' }}>
            <button
              className="btn btn--primary"
              onClick={() => {
                setOpen(false)
                setExport(true)
              }}
              disabled={nodeCount === 0}
            >
              <Icon name="download" size={13} />
              Share or export this exploration
            </button>
            <button
              className="btn"
              onClick={() => fileRef.current?.click()}
              title="Opens an exploration’s JSON export or a recovery copy as a new exploration. Nothing stored is replaced."
            >
              <Icon name="folder" size={13} />
              Open a Pathways file…
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              aria-hidden
              tabIndex={-1}
              onChange={(e) => {
                const f = e.target.files?.[0]
                // Cleared before the read, so choosing the same file again fires.
                e.target.value = ''
                if (f) void openFile(f)
              }}
            />
          </div>

          {rows.length > 0 && (
            <ul className="docs">
              {listed.map((e) => (
                <li key={e.id}>
                  <div className="docs-row">
                    {/* A flex row rather than a bare <b>: the chip beside the seed
                        needs the same gap it gets in the model picker, and the seed
                        can be long enough to want the ellipsis. */}
                    <b style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s2)' }}>
                      {/* The name somebody gave it, when they gave one, with the
                          seed a hover away — and otherwise the WHOLE seed, as
                          D105 keeps it here: this is the fuller view. */}
                      <span
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={e.name ? e.seed : undefined}
                      >
                        {titleOf(e)}
                      </span>
                      {e.id === currentId && (
                        <span className="mrow-lack" style={{ color: 'var(--accent)' }}>
                          open now
                        </span>
                      )}
                      {e.demo && e.id !== currentId && <span className="mrow-lack">the demo</span>}
                    </b>
                    <span className="srcs-meta">
                      {/* Where a fork came from, first: it is what tells it from
                          the row it was forked from, which the counts after it
                          may not. The sidebar's words, through the one helper. */}
                      {e.forkedFrom ? `${forkLine(e.forkedFrom)} · ` : ''}
                      {e.nodes} node{e.nodes === 1 ? '' : 's'}
                      {e.contradictions > 0 &&
                        ` · ${e.contradictions} contradiction${e.contradictions === 1 ? '' : 's'}`}
                      {/* A recorded result is the one thing in a document that cannot
                          be re-obtained by paying for another pass, so it is worth
                          naming in a list where the next action might be Forget. */}
                      {e.outcomes
                        ? ` · ${e.outcomes} recorded result${e.outcomes === 1 ? '' : 's'}`
                        : ''}
                      {' · '}
                      saved <Ago at={e.savedAt} /> · {fmtSize(e.chars)}
                    </span>
                  </div>
                  {confirming === e.id ? (
                    <div className="docs-confirm">
                      {/* The sidebar's sentence, from the one place both say it
                          (D335), with the recorded-results clause a graph that
                          can be reasoned out again does not need. */}
                      <span>
                        {forgetSentence({ outcomes: e.outcomes, isOpen: e.id === currentId })}
                      </span>
                      <button
                        className="btn btn--bad"
                        disabled={forgettingOne}
                        aria-busy={forgettingOne}
                        onClick={() => {
                          // Through the one path every Forget door shares
                          // (D331): the canvas first when this is the one open,
                          // then the deletion, awaited, and the conversations
                          // only once the shelf has accepted it. The sentence is
                          // about what the shelf answered.
                          void forgetExploration(e.id).then((out) => {
                            setConfirming((c) => (c === e.id ? null : c))
                            refresh()
                            const line = forgetLine(out)
                            if (line) toast(line.text, line.tone)
                          })
                        }}
                      >
                        {forgettingOne ? 'Forgetting…' : 'Forget'}
                      </button>
                      <button
                        ref={keepRef}
                        className="btn"
                        disabled={forgettingOne}
                        onClick={() => setConfirming(null)}
                      >
                        Keep
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, flex: 'none' }}>
                      {e.id !== currentId && (
                        <button
                          className="btn"
                          onClick={() => openOne(e.id)}
                          // Named for its row, as Forget beside it is: a list of
                          // twelve buttons all called "Open" is a list a screen
                          // reader cannot choose from.
                          aria-label={`Open ${titleOf(e)}`}
                        >
                          Open
                        </button>
                      )}
                      <button
                        className="btn"
                        style={{ color: 'var(--text-3)' }}
                        onClick={() => setConfirming(e.id)}
                        // The pinned demo is replayable from the sidebar, so forgetting
                        // it is not destructive in the way the others are — but it is
                        // still the user's to remove.
                        aria-label={`Forget ${titleOf(e)}`}
                      >
                        Forget
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {rows.length === 0 && (
            <p className="gnote gnote--quiet">
              Nothing stored yet. The demo does not count until you branch from it, and an
              exploration appears here the first time it grows past its seed.
            </p>
          )}

          {/* The one thing in this app that grades the model instead of quoting it.
              It lives here rather than in the sidebar because it is the only figure
              that spans the whole library, which is what this sheet is a view of —
              and the library is also exactly where it stops, which is why each of
              the three blocks below names its shelf. The third does it inside
              `stabilityLine`, which nothing may append to. */}
          {ledger.length > 0 && (
            <>
              <h3 id={LEDGER_ID} style={{ marginTop: 'var(--s4)' }}>
                How well its confidence has tracked reality
              </h3>
              <p className="gnote gnote--quiet">
                Every confidence on the canvas is the model rating its own work. This compares what
                it claimed, before any result existed, against what you actually recorded.
              </p>
              <CalibrationTable ledger={ledger} against="tested" />
              <p className="gnote gnote--quiet">
                These are the claims somebody chose to test, which is not a random sample — people
                test what they doubt and what is cheap to test. Read it as this model's record on
                your questions rather than as its calibration in general. Self-ratings do not
                compare across models, so there is a row each and never a total. It counts the
                explorations listed above.
              </p>
            </>
          )}

          {/* The second table, and the reason it exists: recording a result means
              actually running an experiment, so for almost every exploration the block
              above is empty and the one mechanism here that grades the model instead of
              quoting it never runs. A source-check reading is weaker evidence and comes
              from outside the reasoning pass all the same — it is the same fact
              `/until 3 refuted` is allowed to end a paid run on. Below the results,
              never merged with them. */}
          {checkLedger.length > 0 && (
            <>
              <h3 style={{ marginTop: 'var(--s4)' }}>And against what sources said</h3>
              <p className="gnote gnote--quiet">
                The same comparison over a weaker kind of evidence, kept apart from it for that
                reason. A reading is what a search returned on a date rather than something you
                observed — but its citations were checked against what the search actually returned,
                so it can grade a confidence written before it existed.
              </p>
              <CalibrationTable ledger={checkLedger} against="checked" />
              <p className="gnote gnote--quiet">
                Which claims get checked is chosen by structure — what the rest of the argument
                rests on, then what is most open — so this is not a random sample either. And a
                claim counted as holding up has not been shown to be true; sources appeared to agree
                on the day they were searched. It reads the explorations in this library and stops
                there.
              </p>
            </>
          )}

          {/* The conjecture track record (D282), in the reader's word for one,
              insight (D333). Its own heading and its own list, because it
              answers a different question from both tables above: not whether a
              confidence tracked reality, but how the predictions a model set
              down went where somebody tested them — counted, never scored, and
              only ever among the ones you chose. */}
          {conjectures.length > 0 && (
            <>
              <h3 style={{ marginTop: 'var(--s4)' }}>How its insights fared when tested</h3>
              <p className="gnote gnote--quiet">
                Each insight set down a prediction before anything answered it. This counts the ones
                you recorded a result against, per model that proposed them, and how many went the
                way the prediction said — never a score, and never before {MIN_SCORED} of them have
                a result.
              </p>
              {conjectures.map((r) => (
                <div key={r.key} className="docs-row" style={{ alignItems: 'flex-start' }}>
                  <b>{r.model.name}</b>
                  <span className="srcs-meta">{trackRecordLine(r)}</span>
                </div>
              ))}
              <p className="gnote gnote--quiet">
                You chose which insights to test, so this is a record of those and not a rate at
                which a model's insights hold. It reads the explorations in this library and stops
                there.
              </p>
            </>
          )}

          {/* A sibling of the two tables above and a DIFFERENT question, which is
              why it has its own heading rather than a third column: those grade a
              confidence against something that came from outside the model, and
              this never leaves it. Nothing here is averaged with them, and nothing
              here is stored — the counts are recomputed from the stored panels
              every time this sheet opens, exactly as both ledgers above are. */}
          {stability.length > 0 && (
            <>
              {/* No "And", where both tables above take one: those two are the
                  same question over two grades of evidence, and this is not a
                  third of them. The heading is the report's, word for word. */}
              <h3 style={{ marginTop: 'var(--s4)' }}>How often it agreed with itself</h3>
              <p className="gnote gnote--quiet">
                Not accuracy. When a claim is read again under three lenses, blind to each other,
                this is how often those passes landed in the same place — the steadiness of the
                model's own reasoning. The verdicts are counted; no pass's rating of its own verdict
                is read here at all.
              </p>
              {stability.map((s) => (
                <div key={s.key} className="docs-row" style={{ alignItems: 'flex-start' }}>
                  <b>{s.model.name}</b>
                  <span className="srcs-meta">{stabilityLine(s)}</span>
                </div>
              ))}
              <p className="gnote gnote--quiet">
                Three passes of one model converging on a mistake look exactly like three passes
                agreeing. The divergences are the informative half, and they are on the graph as
                split strands.
              </p>
            </>
          )}

          {space && shelfOpen() && (
            <p className="gnote gnote--quiet">
              {space.count} stored · {fmtSize(space.used)} of stored research.
              {room
                ? ` Browser estimate: ${fmtBytes(room.usage)} used of about ${fmtBytes(room.quota)} available to this site, including other stored data.`
                : ''}{' '}
              A refused write pauses paid work and keeps a recovery copy in memory. Retained history
              is not trimmed to fit a storage quota. Explorations stay on this device; an export or
              share link creates a separate copy, and clearing this site's data removes local work.
            </p>
          )}
          {space && !shelfOpen() && (
            <p className="gnote gnote--quiet">
              {space.count} held in memory for this tab only · {fmtSize(space.used)}. This browser
              could not open its storage. Export this working copy before closing the tab. Any
              previously stored explorations remain untouched for a later successful open.
            </p>
          )}

          {rows.length > 0 &&
            canForgetEverything() &&
            (confirmAll ? (
              <div className="docs-confirm">
                {/* The results clause is the loudest sentence on the sheet: a
                    graph can be reasoned out again, and a recorded result is a
                    trip to a lab. The keys clause says what is NOT swept (D335). */}
                <span>
                  {forgetAllSentence({
                    count: rows.length,
                    withResults: rows.some((e) => (e.outcomes ?? 0) > 0),
                    keysKept,
                  })}
                </span>
                <button
                  className="btn btn--bad"
                  disabled={forgettingAll}
                  aria-busy={forgettingAll}
                  onClick={() => {
                    // The same path, for everything: the open one is cleared
                    // first, as this sentence promises, and the conversations
                    // go only once the shelf has accepted the clear (D331).
                    void forgetEverything().then((out) => {
                      setConfirmAll(false)
                      refresh()
                      const line = forgetAllLine(out, keysKept)
                      // The way to the keys from the sentence that names them:
                      // a reader clearing a borrowed machine is one press from
                      // finishing the job (D335).
                      if (line)
                        toast(
                          line.text,
                          line.tone,
                          line.keysKept
                            ? {
                                label: 'Remove keys',
                                run: () => {
                                  const s = useStore.getState()
                                  s.setLibrary(false)
                                  s.setSetup('settings', 'providers')
                                },
                              }
                            : undefined,
                        )
                    })
                  }}
                >
                  {forgettingAll ? 'Forgetting…' : 'Forget all'}
                </button>
                <button
                  ref={keepRef}
                  className="btn"
                  disabled={forgettingAll}
                  onClick={() => setConfirmAll(false)}
                >
                  Keep them
                </button>
              </div>
            ) : (
              <button
                className="btn"
                style={{ color: 'var(--text-3)', alignSelf: 'flex-start' }}
                onClick={() => setConfirmAll(true)}
              >
                <Icon name="trash" size={13} />
                Forget every exploration
              </button>
            ))}
        </div>

        <div className="sheet-actions">
          <button className="btn btn--primary" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
